import { useField } from 'formik';
import { ChangeEvent } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Details from '../../forms/Details';
import SummaryDisplay from '../../SummaryDisplay';
import TextReadOnly from '../TextReadOnly';
import { SelectInputProps } from './types';

const SelectInput = <T extends string | number>(selectInputProps: SelectInputProps<T>) => {
    const {
        displayHorizontally,
        label,
        inlineHelp = undefined, // default props
        inlineHelpTitle,
        options,
        defaultValue = '', // default props
        defaultDisplayText,
        className = '', // default props
        containerClassName = '', // default props
        name,
        id,
        disabled,
        onChange,
        isSummary,
        readOnly,
        addBlank,
    } = selectInputProps;

    const [field, meta] = useField(name);
    const helpId = inlineHelp ? `help-${id || name}` : undefined;

    const defaultUIOption = (
        <option
            value={defaultValue}
            key={defaultValue}
            hidden={false}
        >
            {defaultDisplayText || 'Please select'}
        </option>
    );
    if (isSummary) {
        if (field && field.value) {
            return (
                <SummaryDisplay
                    label={label}
                    id={id || name}
                    as='p'
                    value={options?.find((x) => x.value === field.value)?.displayText || ''}
                />
            );
        }
        return null;
    }

    if (readOnly === true) {
        return (
            <TextReadOnly
                label={label}
                name={name}
                inlineHelp={inlineHelp}
                inlineHelpTitle={inlineHelpTitle}
                value={options?.find((x) => x.value === field.value)?.displayText || ''}
            />
        );
    }

    const onSelectChange = async (event: ChangeEvent<HTMLInputElement>) => {
        if (onChange !== undefined) {
            onChange(event);
        }
        field.onChange(event);
    };

    return (
        <Form.Group controlId={id || name} className='form-field-container'>
            {displayHorizontally
                ? (
                    <Row>
                        <Col md={6} className='align-self-top align-self-md-center'>
                            <Form.Label className={inlineHelp ? 'pb-0' : ''}>{label}</Form.Label>
                            {inlineHelp && !inlineHelpTitle
                && (
                    <Form.Text as='p' id={helpId} className='contextual-help text-muted'>
                        {inlineHelp}
                    </Form.Text>
                )}
                            {inlineHelp && inlineHelpTitle
                && (
                    <Details id={helpId} title={inlineHelpTitle} inlineHelp={inlineHelp} />
                )}
                        </Col>
                        <Col md={6}>
                            <div className={`d-grid gap-2 d-flex justify-content-start justify-content-md-end ${containerClassName}`}>
                                <Form.Control
                                    key={name}
                                    as='select'
                                    disabled={disabled}
                                    className={`form-field form-select ${className}`}
                                    {...field}
                                    onChange={onSelectChange}
                                    isInvalid={!!(meta.touched && meta.error)}
                                    aria-describedby={
                                        meta.touched && meta.error
                                            ? `${id || name}-validation-msg`
                                            : (helpId || undefined)
                                    }
                                >
                                    {addBlank && defaultUIOption}
                                    {options?.map((o) => (
                                        <option
                                            value={o.value}
                                            key={o.value}
                                            className={o.className}
                                            disabled={o.disabled}
                                            hidden={o.hidden}
                                        >
                                            {o.displayText}
                                        </option>
                                    ))}
                                </Form.Control>
                                {meta.touched && meta.error
                                    ? (
                                        <Form.Control.Feedback type='invalid' id={`${id || name}-validation-msg`}>
                                            {meta.error}
                                        </Form.Control.Feedback>
                                    )
                                    : null}
                            </div>
                        </Col>
                    </Row>
                )
                : (
                    <>
                        <Form.Label>{label}</Form.Label>
                        {inlineHelp && !inlineHelpTitle
              && (
                  <Form.Text as='p' id={helpId} className='contextual-help text-muted'>
                      {inlineHelp}
                  </Form.Text>
              )}
                        {inlineHelp && inlineHelpTitle
              && (
                  <Details id={helpId} title={inlineHelpTitle} inlineHelp={inlineHelp} />
              )}
                        <div className={`d-grid gap-2 ${containerClassName}`}>
                            <Form.Control
                                key={name}
                                as='select'
                                disabled={disabled}
                                className={`form-field form-select ${className}`}
                                {...field}
                                onChange={onSelectChange}
                                isInvalid={!!(meta.touched && meta.error)}
                                aria-describedby={
                                    meta.touched && meta.error
                                        ? `${id || name}-validation-msg`
                                        : (helpId || undefined)
                                }
                            >
                                {addBlank && defaultUIOption}
                                {options?.map((o) => (
                                    <option
                                        value={o.value}
                                        key={o.value}
                                        className={o.className}
                                        disabled={o.disabled}
                                        hidden={o.hidden}
                                    >
                                        {o.displayText}
                                    </option>
                                ))}
                            </Form.Control>
                            {meta.touched && meta.error
                                ? (
                                    <Form.Control.Feedback type='invalid' id={`${id || name}-validation-msg`}>
                                        {meta.error}
                                    </Form.Control.Feedback>
                                )
                                : null}
                        </div>
                    </>
                )}
        </Form.Group>
    );
};

// SelectInput.defaultProps = {
//     inlineHelp: undefined,
//     defaultValue: '',
//     className: '',
//     containerClassName: '',
// };

export default SelectInput;
